<template>
  <div type="outlined" class="flex flex-col gap-0.5">
    <div
      class="bg-surface-container rounded-t-large rounded-b-extra-small flex h-13 flex-row items-center pr-1.5 pl-4"
    >
      <div class="flex grow flex-col">
        <h3 class="text-title-medium">Testimonials</h3>
        <h3 class="text-body-small text-on-surface-variant -mt-0.5">Wähle aus deinen spontanen Feedbacks aus</h3>
      </div>
      <BaseTextButton
        v-if="selectedSpontaneousFeedbacks.length > 0"
        color="tonalPrimary"
        icon="edit"
        size="S"
        class="mx-auto"
        @click="openDialog"
        >Anpassen</BaseTextButton
      >
    </div>
    <div
      v-if="selectedSpontaneousFeedbacks.length == 0"
      class="rounded-t-extra-small rounded-b-large bg-surface-container flex w-full items-center justify-center p-4"
    >
      <BaseIconButton
        color="tonalPrimary"
        icon="add"
        size="M"
        width="wide"
        class="mx-auto"
        @click="openDialog"
      />
    </div>
    <div class="flex grow flex-col">
      <ul class="flex flex-col gap-0.5">
        <li
          v-for="feedback in selectedSpontaneousFeedbacks"
          :key="feedback.id"
          class="rounded-extra-small last:rounded-b-large bg-surface-container flex flex-col gap-2 p-4"
        >
          <TextFeedbacksForPerspectives
            :perspectives="[
              {
                perspective: spontaneousPerspective,
                numberOfFeedbacks: 1,
                numberOfSenders: feedback.sender ? 1 : 0,
                texts: [feedback],
              },
            ]"
          />
        </li>
      </ul>
    </div>
    <SelectSpontaneousFeedbacksDialog
      :is-open="isOpen"
      :spontaneous-feedbacks="spontaneousFeedbacksWithSender"
      :initial-spontaneous-feedback-ids="selectedSpontaneousFeedbackIds"
      @close="closeDialog"
      @select-spontaneous-feedbacks="selectSpontaneousFeedbacks"
    />
  </div>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import BaseIconButton from '../../../core/components/base/button/icon-button/BaseIconButton.vue'
  import SelectSpontaneousFeedbacksDialog from './SelectSpontaneousFeedbacksDialog.vue'
  import type { JSONContent } from '@tiptap/core'
  import TextFeedbacksForPerspectives from '@/core/components/analytics/text-feedback/TextFeedbacksForPerspectives.vue'
  import { useMembersStore } from '@/stores/members.store'
  import type { Perspective } from '@/core/models/perspective'

  const spontaneousPerspective: Perspective = {
    id: 'peers',
    key: 'peers',
    title: 'Peers',
    nameLong: 'Peers|Peer|Peers',
    nameShort: 'Peers|Peer|Peers',
    icon: 'groups',
    allowedMechanisms: ['app_user', 'link'],
  }

  interface Props {
    spontaneousFeedbacks: ReadonlyArray<{
      id: string
      senderId?: string
      text: JSONContent
      date: string
    }>
  }
  const props = defineProps<Props>()

  const { isOpen, openDialog, closeDialog } = useDialog()

  const selectedSpontaneousFeedbackIds = defineModel<string[]>({ required: true })

  const memberStore = useMembersStore()

  const spontaneousFeedbacksWithSender = computed(() => {
    return props.spontaneousFeedbacks.map((feedback) => ({
      ...feedback,
      sender: feedback.senderId
        ? (memberStore.getMemberById(feedback.senderId) ?? undefined)
        : undefined,
    }))
  })

  const selectedSpontaneousFeedbacks = computed(() => {
    return spontaneousFeedbacksWithSender.value.filter((feedback) =>
      selectedSpontaneousFeedbackIds.value.includes(feedback.id),
    )
  })

  const selectSpontaneousFeedbacks = (feedbackIds: string[]) => {
    selectedSpontaneousFeedbackIds.value = feedbackIds
  }
</script>
